<template>
<v-container class="pt-0 full-height">
  <v-row class="align-center mb-3 mt-3 no-gutters">
    <v-col cols="12" sm="8" md="9">
      <h1 class="section-title primary--text mb-0">Corte de Caja: Sucursal {{ storeName }}</h1>
    </v-col>
    <v-col cols="12" sm="4" md="3" class="d-flex justify-end">
      <v-btn
      v-if="hasOrders"
      @click="makeCashClose"
      color="primary"
      class="px-4"
      dark
      >
        <v-icon left>mdi-lock</v-icon>
        Cerrar Caja
      </v-btn>
    </v-col>
  </v-row>

  <v-row class="mb-4">
    <v-col cols="12" md="4">
      <cash-summary-card
        title="Total Caja Inicio"
        :amount="cashStartAmountSafe"
      />
    </v-col>
    <v-col cols="12" md="4">
      <cash-summary-card
        title="Total Caja Fin"
        :amount="totalCajaFin"
      />
    </v-col>
    <v-col cols="12" md="4">
      <cash-summary-card
        title="Total General"
        :amount="totalGeneralConInicio"
      />
    </v-col>
  </v-row>

  <purchase-order-table
  v-if="hasCashCloseData"
  :orders="cashCloseData.orders"
  :totals="tableTotals"
  :invoice-summary="cashCloseData.invoiceSummary"
  :loading="isTableLoading"
  @view-order="onViewOrder"
  @delete-order="onDeleteOrderSuccess"
  @filters-change="onTableFiltersChange"
  />

  <purchase-order-details-modal></purchase-order-details-modal>
</v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Swal from 'sweetalert2';
import PurchaseOrderService from '../services/PurchaseOrderService';
// import MoneyTotalCard from '../components/CashClose/MoneyTotalCard.vue';
// import PurchaseOrderCard from '../components/CashClose/PurchaseOrderCard.vue';
import PurchaseOrderDetailsModal from '../components/CashClose/PurchaseOrderDetailsModal.vue';
import { FPUtils } from '../utils/FloatingPointUtils';
import CashSummaryCard from '../components/CashClose/CashSummaryCard.vue';
import PurchaseOrderTable from '../components/CashClose/PurchaseOrderTable.vue';

export default {
  name: 'cash-closing',
  components: {
    PurchaseOrderDetailsModal,
    CashSummaryCard,
    PurchaseOrderTable,
  },
  data: () => ({
    storeName: '',
    orderId: null,
    orderIndex: null,
    tableFilters: {
      q: '',
      paymentMethod: null,
      invoiceStatus: null,
    },
    isTableLoading: false,
  }),
  computed: {
    ...mapGetters({
      cashCloseData: 'cashCloseData',
      authUserData: 'authUserData',
    }),
    totalCajaFin() {
      if (!this.cashCloseData) return '$0.00';
      const inicio = Number(this.cashCloseData.cashStartAmount || 0);
      const efectivo = Number(this.cashCloseData.cashAmount || 0);
      return this.formatMoney(inicio + efectivo);
    },
    totalGeneralConInicio() {
      if (!this.cashCloseData) return '$0.00';
      const inicio = Number(this.cashCloseData.cashStartAmount || 0);
      const recibidoTotal = Number(this.cashCloseData.totalAmount || 0);
      return this.formatMoney(inicio + recibidoTotal);
    },
    tableTotals() {
      if (!this.cashCloseData) {
        return {
          cash: '$0.00',
          debit: '$0.00',
          credit: '$0.00',
          check: '$0.00',
          transfer: '$0.00',
        };
      }
      return {
        cash: this.cashCloseData.cashAmountStr,
        debit: this.cashCloseData.debitCardAmountStr,
        credit: this.cashCloseData.creditCardAmountStr,
        check: this.cashCloseData.checkAmountStr,
        transfer: this.cashCloseData.transferAmountStr,
      };
    },
    hasCashCloseData() {
      return !!(this.cashCloseData && Array.isArray(this.cashCloseData.orders));
    },
    hasOrders() {
      return this.hasCashCloseData && this.cashCloseData.orders.length > 0;
    },
    cashStartAmountSafe() {
      return this.cashCloseData?.cashStartAmountStr || '$0.00';
    },
  },
  methods: {
    ...mapActions(['showOrderDetails']),

    onViewOrder(order) {
      this.showOrderDetails(order);
    },

    onTableFiltersChange(filters) {
      this.tableFilters = { ...this.tableFilters, ...filters };
      this.setupData();
    },

    async onDeleteOrderSuccess() {
      await this.setupData();
    },

    formatMoney(value) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(value || 0);
    },

    async setupData() {
      const { store } = this.authUserData.user_claims;
      this.isTableLoading = true;

      try {
        const data = await PurchaseOrderService.getCashCloseData(store, {
          q: this.tableFilters.q || null,
          paymentMethod: this.tableFilters.paymentMethod || null,
          invoiceStatus: this.tableFilters.invoiceStatus || null,
        });
        this.$store.dispatch('setCashCloseData', data);
      } catch (e) {
        this.$store.dispatch('setCashCloseData', {
          orders: [],
          cashStartAmount: 0,
          totalAmount: 0,
          cashAmount: 0,
          debitCardAmount: 0,
          creditCardAmount: 0,
          checkAmount: 0,
          transferAmount: 0,
          invoiceSummary: { pending: 0, particular: 0, global: 0 },
        });
      } finally {
        this.isTableLoading = false;
      }
    },
    async makeCashClose() {
      let { totalAmount } = this.cashCloseData;
      totalAmount = FPUtils.round2dec(totalAmount);

      const { store } = this.authUserData.user_claims;

      Swal.fire({
        allowOutsideClick: false,
        title: 'Proceder a Corte de caja',
        confirmButtonText: 'Si',
        text: '¿Hacer corte de caja? Esta operación no se puede deshacer',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'No',
        showLoaderOnConfirm: true,
        preConfirm: () => PurchaseOrderService.CashClose(totalAmount, store)
          .then((resp) => {
            if (!resp.error) {
              Swal.fire('Correcto', 'Se hizo el corte de caja', 'success');
              this.setupData();
            } else {
              Swal.fire({
                title: 'Error',
                text: 'No se pudo hacer el corte de caja',
                icon: 'error',
              });
            }
          }),
      });
    },
  },
  async mounted() {
    this.setupData();
    this.storeName = localStorage.getItem('storeName');
  },
};
</script>

<style scoped>
.fixed-row {
  position: fixed;
  z-index: 1;
}
.section-title {
  font-weight: 300;
  font-size: 2.5rem;
}
.section-subtitle {
  font-weight: 300;
  font-size: 2rem;
}

.headline {
  font-size: 1.8rem!important;
}
.orders-container {
  display: flex !important;
  height: 100% !important;
  flex-flow: column !important;
}
</style>
